// Understanding the radar data (Radar analysis, at the end of the page): short
// explainers under disclosures, in plain words, with what nodes 1 and 2
// showed, and how rich readings (firmware .58) arrive. Static text; the
// figures are node 1's unless said otherwise.
const EXPLAINERS = [
  [
    "What the spectrum is",
    <>
      <p>
        256 numbers from the radar: how strongly something reflects at each
        distance, one per range cell of about 31.55 mm. The radar works it out
        itself, as an FFT of the beat signal with no window.
      </p>
      <p>
        On node 1 the empty air sits near 60 dB, the radar's own near field at
        81-83 dB and the water at about 99 dB near 800 mm: a tall, narrow peak.
        A dry, uneven floor gives a lower, broader hump; the rim and joints of
        the pipe give peaks that never move.
      </p>
    </>,
  ],
  [
    "What the beat signal is",
    <>
      <p>
        The radar is an FMCW radar: for each measurement it sends a chirp, a
        tone sweeping about 5 GHz upwards around 80 GHz (a wavelength of about
        3.75 mm). The receiver mixes every echo with the chirp it is still
        sending (“dechirping”): the echo comes back a little late, when the
        chirp has moved on, so the mix is a tone whose frequency is set by the
        echo's distance. The 512 samples are taken across the sweep, so each is
        the scene seen at one frequency step, and an echo at distance R from the
        radar's own zero makes R / 31.55 mm cycles across the record - about
        25.7 for node 1's water. The spectrum is this signal split into its
        tones.
      </p>
      <p>
        Our FFT of node 1's beat signal reproduces the radar's spectrum (r =
        0.998 in dB, the same peak cells), and can do better: a window (Hann)
        separates a weak echo from a strong one beside it, zero-padding (×8)
        places an echo to a fraction of a cell, and clipping, invisible in the
        spectrum, shows in the samples themselves.
      </p>
    </>,
  ],
  [
    "Real samples, not I/Q",
    <p>
      The radar records one real channel, not the in-phase and quadrature pair
      (I/Q) some radars have. The evidence: our FFT of its 512 real samples
      reproduces its spectrum (r = 0.998). Real sampling gives 256 usable range
      cells (the other half of the FFT mirrors them) and about 3 dB more noise
      than an I/Q receiver would, but the FFT still gives a complex value - an
      amplitude and a phase - for every cell. The radar's own spectrum keeps
      only the amplitude; the beat signal keeps the phase too.
    </p>,
  ],
  [
    "Why both are kept for now, and which to keep later",
    <>
      <p>
        Each is about 1 kB from the radar (1,042 B a spectrum, 1,036 B a beat
        signal), and the radar sends one or the other in each raw output mode,
        so a capture holds one of them; alternation takes them in turns.
      </p>
      <p>
        The beat signal is the richer at the same size: the spectrum can be
        computed from it, never the other way round. The spectrum carries the
        radar's own pick (its peak cell and reported distance) and checks that
        our FFT matches the radar's. Once that has held at a site, keeping only
        the beat signal is the likely choice - and processing on the node itself
        would start from the beat signal too.
      </p>
      <p>
        For our estimate the beat signal is the precise path. A burst of spectra
        alone places each echo only approximately, from a parabola over the
        radar's cells: on node 1 those positions sit about a cell (≈31.5 mm)
        long, and the page marks them ≈. Bursts that alternate the spectrum with
        the beat signal give the precise position and keep the radar's own
        spectrum as a check.
      </p>
    </>,
  ],
  [
    "The distance axis",
    <p>
      Distance = cell × RangeRes − the calibration offset: 31.55 mm a cell and
      19 mm on nodes 1 and 2 (the radar's own AT+CALIBRATION). That is how the
      radar turns a cell into the distance it reports, so the marks on these
      charts line up with the node's readings. The beat signal confirms it: our
      zero-padded FFT puts node 1's water at 789 mm against the radar's 781 mm,
      within a quarter of a cell.
    </p>,
  ],
  [
    "Phase = interferometry",
    <>
      <p>
        The phase of an echo's tone moves with its distance, as in satellite
        radar interferometry (InSAR): Δphase = 4π ΔR / λ. One full turn of phase
        is 1.87 mm of range, one degree 5.2 µm. On node 1 overnight, 517 beat
        signals of still water held their phase to 0.11° (0.6 µm) per capture
        and drifted steadily by 21.3 µm an hour (0.13 mm in 6 h) while the board
        stayed at about 42 °C - not thermal, most likely evaporation - and the
        radar's own reading went from 781.2 to 781.3 mm.
      </p>
      <p>
        The limits: the phase wraps every 1.87 mm, so only changes under about
        0.9 mm between captures can be followed (beyond that it has to be
        combined with the coarse position); it needs a calm, steady target -
        ripple or rain scramble it, as time scrambles InSAR; powering the radar
        down between wakes may reset its phase reference (still to be tested);
        and another echo in the same cell biases it.
      </p>
    </>,
  ],
  [
    "Why the phase matters here",
    <p>
      It measures what the radar's millimetre readings cannot: evaporation and
      seepage rates of tens of micrometres an hour; the first water arriving, as
      a phase that starts to move before the reading does; a calm or rippling
      surface, from how steady the phase is; and fixed structures from water - a
      joint or the rim keeps a constant phase, like a persistent scatterer in
      InSAR, while water drifts. Micro-movement, above, follows it.
    </p>,
  ],
  [
    "Clipping",
    <p>
      A strong, close echo overloads the receiver: the tops of the waveform are
      cut flat, early in each sweep (about its first 40 %). On node 1 that is
      6.6 % of the samples, at about ±7,100 counts - the analog stage saturates,
      well inside the 16-bit range. A clipped tone is no longer a pure tone, so
      the spectrum gains echoes at 2, 3 and 4 times the true distance that are
      not there. It is detectable: the samples dwell flat longer than any sine
      could, the waveform shades that part, and our analysis labels those echoes
      harmonic.
    </p>,
  ],
  [
    "The radar's statistical filter",
    <p>
      The radar smooths its own reading. On node 1 it is set to LIMITFILTEROFXCM
      10cm,30: a jump of more than 10 cm is accepted only after it has lasted 30
      measurements, about 4.8 s at its 160 ms measurement period. That rejects a
      stray reading but also holds on to whatever it locked onto - and the node
      powers the radar for only a few seconds each wake, so the radar rarely
      gets 30 measurements to change its mind. Once locked onto a false echo, it
      can report it wake after wake.
    </p>,
  ],
  [
    "The near field and the double bounce",
    <p>
      The first cells (0-2, about the first 60 mm) hold the radar's own leakage
      and its lens: 81-83 dB on node 1, louder than most echoes. Anything on the
      lens - a film, drops, a spider - raises them, so the trends follow
      near-field energy as a sign of fouling. The double bounce: the signal goes
      down to the water, back up to the radar's face and down again, so a copy
      of a strong echo appears at twice its distance (on node 1 only 5.5 dB
      weaker) and another at three times. Real echoes of a real surface, at the
      wrong distance: our analysis labels them harmonic.
    </p>,
  ],
  [
    "Flicker",
    <p>
      How much the spectrum changes between the captures of one burst, cell by
      cell (the standard deviation, in dB). A still scene flickers about 0.2 dB
      on node 1. Insects or drops crossing the beam, ripple on the water or wind
      in the pipe flicker far more, and only at their own distance: steady water
      is a tall peak that hardly flickers, a passing insect a peak in one
      capture only.
    </p>,
  ],
  [
    "How our estimate works",
    <>
      <p>
        The server analyses every burst once it has arrived, in the shadow of
        the radar's own reading:
      </p>
      <ol>
        <li>
          The echoes: from the beat signals when the burst has any (a Hann
          window, zero-padded ×8, so each echo is placed to a fraction of a
          cell), else from the radar's spectra (approximate, marked ≈). An echo
          counts from 8 dB over the noise floor.
        </li>
        <li>
          Each echo gets a label. Near field: the first cells, the radar's own
          leakage and its lens. Harmonic: at twice the strongest echo in the
          pipe (three or four times while the beat signal clips). Below the
          floor: beyond the pipe's length (+3 %), where the floor cannot lie - a
          longer bounce path or soil layering, never the floor or water. Floor:
          within 8 % of the expected floor; without one (a node not yet
          commissioned), the strongest strong, steady echo in the pipe's last 15
          %, else the farthest strong, steady echo inside the pipe. Clutter:
          where the empty-pipe map has an echo (see Commissioning) or, without a
          map, at the same range in 90 % of the node's last 24 bursts - fixed
          structure. Water: nearer than the floor, and only when clearly
          stronger than a floor that still answers at its usual level (10 dB,
          allowing for range), and not outshone by an echo behind it.
        </li>
        <li>
          The floor rule: 80 GHz does not pass through water, so while the floor
          answers at its usual level there is no water over it - the pipe is
          dry, whatever nearer echo the radar picked. An echo where the floor
          should be that stands well above its usual level is shallow water.
        </li>
        <li>
          The state: wet with a water echo, dry with the floor and none, else
          unknown. The confidence starts at 100 and loses points for each reason
          shown with it - no empty-pipe map, too few earlier bursts, a floor
          taken from the pipe length (commission the node), spectra only, a weak
          or flickering echo, clipping, no range resolution or calibration from
          the radar.
        </li>
      </ol>
      <p>
        One sentence (why) says how the burst was decided; the reasons list what
        cost confidence. That is how node 2 can be read: its radar reports a
        steady echo about 120 mm below it, held there by its filter, while its
        floor should be near 1,170 mm. Once a map, or six of its bursts, show
        that echo as fixed structure, it is labelled clutter, and the floor
        answering further down says the pipe is dry. Its first real burst, on 10
        October, also showed echoes at 1,239 and 1,287 mm, beyond its 1.2 m
        pipe: below the floor. Taking one of them for the floor had made the
        floor itself look like water; now the burst reads dry at 1,170 mm. On
        node 1's real captures of 9 October our distance is 790.7 mm, the
        radar's 781.3 mm.
      </p>
    </>,
  ],
  [
    "The interim rule “impossibly close = dry”, and where it fails",
    <p>
      Until our estimate is trusted, the team reads an impossibly close reading
      as dry: node 2 reports a steady 120 mm, nearer than water can be in its
      pipe, so the pipe is taken to be dry. That holds while the pipe is dry. It
      fails when water comes but the radar stays locked on the near echo, which
      its filter makes likely within a short wake: the reading still says 120
      mm, the rule still says dry, and the water goes unseen. The spectrum shows
      the difference - with water the floor echo goes and a new peak rises above
      it - which is what our estimate, and the bursts on this page, look for.
    </p>,
  ],
  [
    "Rich readings: a capture on every wake",
    <>
      <p>
        From firmware rtct.58 the node takes a raw capture on every normal wake:
        the beat signal, the spectrum or both, each part a capture of its own
        (the radar sends one kind of packet per raw output mode), and if asked
        more on the wakes that upload. It keeps them and sends them with its
        uploads, one small burst a wake marked “rich”. On an hourly upload six
        or seven wakes arrive together, so the newest reading can be up to an
        hour old without anything being wrong, and the wakes since the last
        upload are still on the node.
      </p>
      <p>
        The recommended setup - beat signal and spectrum on every wake, nothing
        extra on upload wakes, the classic hourly bursts off - brings 144 wakes
        and 288 captures a day on a 10-minute wake, about 5.7 kB a wake (0.83 MB
        a day). Every beat signal feeds micro-movement; the spectrum keeps the
        radar's own view beside ours. With one spectrum a wake there is no
        flicker within a wake (it compares two spectra or more); the history
        follows the spectrum from wake to wake instead.
      </p>
      <p>
        The battery floor (3,000-3,400 mV, 3,200 by default) holds for rich
        readings and bursts alike: a readable cell under it skips them. The node
        counts its captures - taken, failed, waiting for the next upload and
        dropped - and reports them with its settings.
      </p>
    </>,
  ],
];

export default function RadarExplainers() {
  return (
    <section
      className="field-panel field-explain"
      aria-labelledby="radar-explain-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="radar-explain-title">Understanding the radar data</h2>
          <p>Short answers, with what nodes 1 and 2 showed</p>
        </div>
      </div>
      <div className="field-panel-body field-explain-list">
        {EXPLAINERS.map(([title, body]) => (
          <details key={title} className="field-explain-item">
            <summary>{title}</summary>
            <div className="field-explain-body">{body}</div>
          </details>
        ))}
      </div>
    </section>
  );
}
